/**
 * Ana Sayfa.
 *
 * A control centre made only of facts the backend measured.
 *
 * ## What this costs
 *
 * **Two requests, whatever the account holds.** `/workspace/summary` for the
 * totals and `/projects?limit=6` for the recent list. The project list carries
 * each project's evaluated workflow, so "Devam Et", "Dikkat gerekiyor" and
 * every card's state come out of a response that had already arrived - there
 * is no per-card fetch and no request that grows with the number of projects.
 *
 * The previous version asked for a project's sessions, its coverage and its
 * reconstruction on top of the list, which was three more requests to draw
 * one hero card and would have become three per card the moment anybody
 * generalised it.
 *
 * ## What is deliberately not here
 *
 * **Project thumbnails.** The backend serves media previews per asset, but a
 * grid of six previews is six more requests for decoration, and a placeholder
 * that looks like a preview teaches the wrong thing about what the system has.
 *
 * **Worker and server health pills.** Only the API answers `/health`. Two more
 * green dots would be decoration asserting facts nobody measured.
 *
 * **A "3D Model" count that includes sparse reconstruction.** `model_count`
 * comes from validated mesh artifacts and reads zero on every deployment that
 * exists today. That zero is the measurement. Sparse is counted separately and
 * labelled as what it is.
 */

import { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getWorkspaceSummary, listProjects } from "@/api/client";
import type { ProjectListItem, WorkspaceSummary } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import { IconCamera, IconCube, IconFiles, IconLayers, IconPlus } from "@/components/icons";
import { PageHeader, Section, SummaryTile } from "@/components/product";
import { Button, EmptyState, Loading } from "@/components/ui";
import { subjectTypeLabel } from "@/i18n/terms";
import { formatDateTime } from "@/lib/format";
import { useAsync } from "@/lib/useAsync";
import { paths } from "@/app/paths";
import { AttentionList, NextActionPanel } from "@/features/workflow/WorkflowViews";
import { Greeting } from "./Greeting";
import "@/features/projects/projects.css";
import "./home.css";
import "@/features/workflow/workflow.css";

/** How many projects "Çalışmaya devam et" shows. A shortlist, not the library. */
const RECENT_LIMIT = 6;
/** How many projects the attention panel lists before pointing at Projelerim. */
const ATTENTION_LIMIT = 4;

/**
 * One capture tip, and the evidence behind it.
 *
 * Every sentence here is a property of multi-view reconstruction itself, not a
 * calibrated instruction: none of them names a number of photographs or a
 * degree step, because this system has never measured what those should be.
 * The rotation is by day so the page does not change while somebody reads it.
 */
const CAPTURE_TIPS = [
  "Aynı noktadan çok sayıda görüntü almak yerine kamerayı hareket ettirin; rekonstrüksiyon farklı bakış açılarından beslenir.",
  "Komşu görüntüler arasında örtüşme bırakın; birbirine hiç benzemeyen kareler eşleştirilemez.",
  "Netlik en belirleyici etkendir. Bulanık bir kare, fazladan bir açıdan daha az işe yarar.",
  "Işığı sabit tutmaya çalışın; kare kare değişen pozlama eşleştirmeyi zorlaştırır.",
] as const;

function captureTip(): string {
  const day = Math.floor(Date.now() / 86_400_000);
  return CAPTURE_TIPS[day % CAPTURE_TIPS.length]!;
}

/**
 * The four numbers, straight from the workspace aggregate.
 *
 * "Seyrek 3D" and "3D Model" are two tiles, never one. Adding them would be
 * the single most misleading number this product could print: a sparse point
 * cloud is a real result and is not a model anybody can open.
 */
function Metrics({ summary }: { summary: WorkspaceSummary }) {
  const navigate = useNavigate();
  return (
    <div className="grid-4">
      <SummaryTile
        title="Proje"
        icon={<IconLayers size={20} />}
        tone="accent"
        state={`${summary.project_count}`}
        detail="Toplam proje sayısı"
        onOpen={() => navigate(paths.projects())}
      />
      <SummaryTile
        title="Medya"
        icon={<IconCamera size={20} />}
        tone="neutral"
        state={`${summary.media_count}`}
        detail={`${summary.photo_count} fotoğraf · ${summary.video_count} video`}
      />
      <SummaryTile
        title="Seyrek 3D"
        icon={<IconFiles size={20} />}
        tone={summary.sparse_project_count > 0 ? "success" : "neutral"}
        state={`${summary.sparse_project_count}`}
        detail={`${summary.reconstruction_job_count} rekonstrüksiyon çalıştırıldı`}
      />
      <SummaryTile
        title="3D Model"
        icon={<IconCube size={20} />}
        // Never success on zero, and never coloured as a failure either:
        // nothing has gone wrong, this deployment simply produces no meshes.
        tone={summary.model_count > 0 ? "success" : "neutral"}
        state={`${summary.model_count}`}
        detail="Doğrulanmış mesh çıktısı olan projeler"
      />
    </div>
  );
}

/** One row of "Çalışmaya devam et": real facts and one way in. */
function ContinueRow({ project }: { project: ProjectListItem }) {
  const workflow = project.workflow ?? null;
  return (
    <li className="continue-row">
      <div className="continue-row__main">
        <h3 className="continue-row__name">
          <Link to={paths.project(project.id)}>{project.name}</Link>
        </h3>
        <p className="continue-row__facts">
          <span className="chip chip--sm">{subjectTypeLabel(project.subject_type)}</span>
          <span>
            {project.media_count} dosya
            {typeof project.photo_count === "number" &&
            typeof project.video_count === "number" &&
            project.media_count > 0
              ? ` (${project.photo_count} fotoğraf · ${project.video_count} video)`
              : ""}
          </span>
          <span className="continue-row__when">
            Son etkinlik {formatDateTime(project.last_activity_at)}
          </span>
        </p>
        {/* The next action as one line. The full panel with its buttons lives
            on the project screen; repeating it here would give the same
            decision two homes. */}
        {workflow && (
          <NextActionPanel workflow={workflow} projectId={project.id} compact />
        )}
      </div>
      <Link className="btn btn--secondary btn--sm" to={paths.project(project.id)}>
        Devam Et
      </Link>
    </li>
  );
}

export function HomeScreen() {
  const navigate = useNavigate();

  const summaryFetcher = useCallback(
    (signal: AbortSignal) => getWorkspaceSummary({ signal }),
    [],
  );
  const projectsFetcher = useCallback(
    (signal: AbortSignal) => listProjects({ limit: RECENT_LIMIT }, { signal }),
    [],
  );

  const { state: summary, reload: reloadSummary } = useAsync(summaryFetcher);
  const { state: projects, reload: reloadProjects } = useAsync(projectsFetcher);

  const list = projects.status === "success" ? projects.data : [];
  // Evidence-backed only: `workflow.attention` is empty unless the backend
  // measured a count or a terminal status. There is no "looks incomplete".
  const needsAttention = list.filter(
    (project) => (project.workflow?.attention.length ?? 0) > 0,
  );

  const loading =
    (summary.status === "loading" && summary.data === null) ||
    (projects.status === "loading" && projects.data === null);

  return (
    <div className="stack">
      <PageHeader
        title="Ana Sayfa"
        subtitle={<Greeting />}
        actions={
          <Button variant="primary" onClick={() => navigate(paths.newProject())}>
            <IconPlus size={16} /> Yeni Proje Oluştur
          </Button>
        }
      />

      {loading && <Loading />}

      {/* The two requests fail independently, and one failing must not blank
          the other half of the page. */}
      {projects.status === "error" && (
        <ErrorState error={projects.error} onRetry={reloadProjects} />
      )}

      {summary.status === "success" && <Metrics summary={summary.data} />}
      {summary.status === "error" && projects.status !== "error" && (
        <ErrorState error={summary.error} onRetry={reloadSummary} />
      )}

      {projects.status === "success" && list.length === 0 && (
        <EmptyState
          title="Henüz bir projeniz yok."
          description="Bir proje oluşturarak çekimlerinizi yüklemeye ve ölçmeye başlayın."
          action={
            <Button variant="primary" onClick={() => navigate(paths.newProject())}>
              Yeni Proje Oluştur
            </Button>
          }
        />
      )}

      {projects.status === "success" && list.length > 0 && (
        <div className="home">
          <div className="stack">
            <Section
              title="Çalışmaya devam et"
              actions={<Link to={paths.projects()}>Tüm projeleri görüntüle</Link>}
            >
              <ul className="continue-list">
                {list.map((project) => (
                  <ContinueRow key={project.id} project={project} />
                ))}
              </ul>
            </Section>
          </div>

          <div className="stack">
            {/*
              Rendered only when something real is wrong. An always-present
              panel that has to be filled is how warnings become wallpaper -
              so when there is nothing, this section does not exist at all.
            */}
            {needsAttention.length > 0 && (
              <Section title="Dikkat gerekiyor">
                <div className="stack">
                  {needsAttention.slice(0, ATTENTION_LIMIT).map((project) => (
                    <div className="attention__project" key={project.id}>
                      <p className="attention__project-name">
                        <Link to={paths.project(project.id)}>{project.name}</Link>
                      </p>
                      <AttentionList
                        items={project.workflow!.attention}
                        projectId={project.id}
                      />
                    </div>
                  ))}
                  {needsAttention.length > ATTENTION_LIMIT && (
                    <p className="muted">
                      <Link to={paths.projects()}>
                        Dikkat gerektiren diğer projeleri görüntüle
                      </Link>
                    </p>
                  )}
                </div>
              </Section>
            )}

            <Section title="Çekim ipucu">
              <p className="home__tip">{captureTip()}</p>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}
